import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Send } from "lucide-react";
import { supportAPI } from "../../api/apiService"; // Correct Import Path

export default function CreateTicketModal({ isOpen, onClose, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [formData, setFormData] = useState({
        subject: "",
        category: "General",
        priority: "medium",
        description: ""
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await supportAPI.createTicket(formData);
            onSuccess();
            onClose();
            setFormData({ subject: "", category: "General", priority: "medium", description: "" });
        } catch (err) {
            console.error("Failed to create ticket", err);
            alert("Failed to create ticket. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setOpenDropdown(null)}>
            <div 
                className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 relative z-[10001]"
                onClick={e => e.stopPropagation()}
            >
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                    <h3 className="font-bold text-lg text-gray-800">New Support Ticket</h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full text-gray-500 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Subject</label>
                        <input
                            type="text"
                            required
                            value={formData.subject}
                            onChange={e => setFormData({ ...formData, subject: e.target.value })}
                            className="w-full p-3 bg-gray-50 rounded-xl outline-none focus:ring-2 focus:ring-blue-100 border border-transparent focus:border-blue-200 transition-all font-medium"
                            placeholder="e.g. Cannot access reports..."
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="relative">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Category</label>
                            <button
                                type="button"
                                onClick={() => {
                                    setOpenDropdown(openDropdown === 'category' ? null : 'category');
                                }}
                                className="w-full p-3 pr-10 bg-gray-50 rounded-xl outline-none focus:ring-4 focus:ring-blue-500/10 border border-gray-200 focus:border-blue-500 font-medium cursor-pointer flex justify-between items-center text-gray-800"
                            >
                                <span>{formData.category}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${openDropdown === 'category' ? 'rotate-180' : ''}`}><path d="m6 9 6 6 6-6"/></svg>
                            </button>
                            {openDropdown === 'category' && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[10005] overflow-hidden py-1">
                                    {["General", "Technical", "Billing", "Feature Request"].map(opt => (
                                        <div 
                                            key={opt}
                                            onClick={() => { setFormData({ ...formData, category: opt }); setOpenDropdown(null); }}
                                            className={`px-4 py-2.5 cursor-pointer hover:bg-blue-50 text-sm font-medium ${formData.category === opt ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                                        >
                                            {opt}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="relative">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Priority</label>
                            <button
                                type="button"
                                onClick={() => {
                                    setOpenDropdown(openDropdown === 'priority' ? null : 'priority');
                                }}
                                className="w-full p-3 pr-10 bg-gray-50 rounded-xl outline-none focus:ring-4 focus:ring-blue-500/10 border border-gray-200 focus:border-blue-500 font-medium cursor-pointer flex justify-between items-center text-gray-800 capitalize"
                            >
                                <span>{formData.priority}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${openDropdown === 'priority' ? 'rotate-180' : ''}`}><path d="m6 9 6 6 6-6"/></svg>
                            </button>
                            {openDropdown === 'priority' && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[10005] overflow-hidden py-1">
                                    {["low", "medium", "high"].map(opt => (
                                        <div 
                                            key={opt}
                                            onClick={() => { setFormData({ ...formData, priority: opt }); setOpenDropdown(null); }}
                                            className={`px-4 py-2.5 cursor-pointer hover:bg-blue-50 text-sm font-medium capitalize ${formData.priority === opt ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                                        >
                                            {opt}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Description</label>
                        <textarea
                            required
                            rows="4"
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                            className="w-full p-3 bg-gray-50 rounded-xl outline-none focus:ring-2 focus:ring-blue-100 border border-transparent focus:border-blue-200 transition-all resize-none"
                            placeholder="Describe your issue in detail..."
                        />
                    </div>

                        <div className="pt-4 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? "Submitting..." : (
                                    <>
                                        <span>Submit Ticket</span>
                                        <Send className="w-4 h-4" />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>,
            document.body
        );
}
